import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import { useDrawingContext } from './DrawingContext';
import { metersToFeet, feetToMeters } from '../../utils/units';
export const BufferConfig = ({ isVisible, onContinue, onCancel }) => {
    const { state, actions } = useDrawingContext();
    const { ui } = state;
    const { distanceUnit, bufferDistanceMeters } = ui;
    const getDisplayDistance = (meters) => {
        if (distanceUnit === 'ft') {
            return { value: Math.round(metersToFeet(meters)), unit: 'ft' };
        }
        else {
            return { value: meters, unit: 'm' };
        }
    };
    const getFreeBufferText = () => {
        if (bufferDistanceMeters <= 0) {
            return 'No buffer — area only';
        }
        if (distanceUnit === 'ft') {
            return `${Math.round(metersToFeet(150))}ft included`;
        }
        else {
            return '150m included';
        }
    };
    // The buffer is optional — the slider can bottom out at 0 (just the area, no
    // hydrology buffer) and reach up to 1500m. It defaults to 150m (see
    // DrawingContext) so most users still get a sensible buffer without acting.
    const getSliderValues = () => {
        if (distanceUnit === 'ft') {
            return {
                min: 0,
                max: Math.round(metersToFeet(1500)),
                step: 10
            };
        }
        else {
            return { min: 0, max: 1500, step: 10 };
        }
    };
    const handleBufferChange = useCallback((value) => {
        const displayValue = parseInt(value, 10);
        if (!isNaN(displayValue) && displayValue >= 0) {
            const metersValue = distanceUnit === 'ft' ? feetToMeters(displayValue) : displayValue;
            actions.setBufferDistance(Math.round(metersValue));
        }
    }, [actions, distanceUnit]);
    if (!isVisible)
        return null;
    return (_jsxs("div", { className: "fixed bottom-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 flex flex-col items-center gap-3 sm:px-4 sm:max-w-[95vw]", children: [_jsx("div", { className: "bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg text-sm text-center w-full sm:max-w-full", children: "Water doesn't stop at your property line. This buffer widens the terrain we analyse to capture flow from the surrounding land \u2014 a wider buffer picks up more upstream catchment but takes longer to process." }), _jsxs("div", { className: "rounded-lg shadow-lg p-2 sm:p-4 border-2 flex flex-col gap-2 sm:gap-4 w-full sm:max-w-lg", style: {
                    backgroundColor: 'var(--sand)',
                    borderColor: 'var(--pewter)'
                }, children: [_jsxs("div", { className: "flex flex-row items-center justify-center gap-2 sm:gap-4", children: [_jsxs("div", { className: "flex flex-col items-center gap-1", children: [_jsxs("div", { className: "text-lg sm:text-xl font-bold", style: { color: 'var(--charcoal)' }, children: [getDisplayDistance(bufferDistanceMeters).value, getDisplayDistance(bufferDistanceMeters).unit] }), _jsx("div", { className: "text-[10px] sm:text-xs opacity-60", style: { color: 'var(--pewter)' }, children: getFreeBufferText() }), _jsx("div", { className: "flex rounded-md overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: ['ft', 'm'].map((u) => {
                                            const active = distanceUnit === u;
                                            return (_jsx("button", { onClick: () => actions.setDistanceUnit(u), className: "px-3 py-0.5 text-[10px] sm:text-xs font-medium transition-colors", style: {
                                                    backgroundColor: active ? 'var(--charcoal)' : 'transparent',
                                                    color: active ? 'var(--sand)' : 'var(--pewter)',
                                                }, children: u }, u));
                                        }) })] }), _jsxs("div", { className: "flex flex-col items-center flex-1 max-w-[200px] sm:max-w-[300px]", children: [_jsxs("div", { className: "w-full mb-1 sm:mb-2", children: [_jsx("input", { type: "range", min: getSliderValues().min, max: getSliderValues().max, step: getSliderValues().step, value: getDisplayDistance(bufferDistanceMeters).value, onChange: (e) => handleBufferChange(e.target.value), className: "w-full h-1.5 sm:h-2 rounded-lg appearance-none cursor-pointer", style: {
                                                    background: `linear-gradient(to right, var(--forest) 0%, var(--forest) ${((getDisplayDistance(bufferDistanceMeters).value - getSliderValues().min) / (getSliderValues().max - getSliderValues().min)) * 100}%, var(--pewter) ${((getDisplayDistance(bufferDistanceMeters).value - getSliderValues().min) / (getSliderValues().max - getSliderValues().min)) * 100}%, var(--pewter) 100%)`,
                                                } }), _jsx("style", { dangerouslySetInnerHTML: {
                                                    __html: `
                  input[type="range"]::-webkit-slider-thumb {
                    appearance: none;
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    background: var(--forest);
                    cursor: pointer;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  }
                  input[type="range"]::-moz-range-thumb {
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    background: var(--forest);
                    cursor: pointer;
                    border: none;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  }
                  @media (min-width: 640px) {
                    input[type="range"]::-webkit-slider-thumb {
                      height: 20px;
                      width: 20px;
                    }
                    input[type="range"]::-moz-range-thumb {
                      height: 20px;
                      width: 20px;
                    }
                  }
                `
                                                } })] }), _jsxs("div", { className: "flex justify-between w-full text-[10px] sm:text-xs opacity-60", style: { color: 'var(--pewter)' }, children: [_jsxs("span", { children: [getSliderValues().min, distanceUnit === 'ft' ? 'ft' : 'm'] }), _jsxs("span", { children: [getSliderValues().max, distanceUnit === 'ft' ? 'ft' : 'm'] })] })] })] }), _jsxs("div", { className: "flex flex-row justify-center gap-2 sm:gap-4", children: [_jsxs("button", { onClick: onCancel, className: "px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors hover:opacity-80 flex-1 text-xs sm:text-base", style: {
                                    backgroundColor: 'transparent',
                                    color: 'var(--pewter)',
                                    border: '1px solid var(--pewter)'
                                }, children: [_jsx("span", { className: "sm:hidden", children: "Back" }), _jsx("span", { className: "hidden sm:inline", children: "Back to Drawing" })] }), _jsxs("button", { onClick: onContinue, className: "px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors flex-1 text-xs sm:text-base", style: {
                                    backgroundColor: 'var(--charcoal)',
                                    color: 'var(--sand)'
                                }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--pewter)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'var(--charcoal)'; }, children: [_jsx("span", { className: "sm:hidden", children: "Continue" }), _jsx("span", { className: "hidden sm:inline", children: "Continue to Contours" })] })] })] })] }));
};
BufferConfig.displayName = 'BufferConfig';
